import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Image as ChakraImage,
  Select,
  Stack,
  Textarea,
  Text,
  useToast,
  VStack,
  ScaleFade,
  Tooltip,
  Badge,
  HStack,
  Switch,
  Grid,
  GridItem,
  Wrap,
  WrapItem,
  Link,
  Spinner,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  IconButton,
  Skeleton,
  Icon,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaDownload, FaExpand, FaChevronLeft, FaChevronRight, FaVideo, FaImage } from 'react-icons/fa';
import { generateImage } from '../services/AIService';
import { fetchGenerationHistory, mapGenerationToViewModel } from '../services/GenerationService';
import { getBackendUrl } from '../config/environments';
import RecentHistoryGrid from '../components/RecentHistoryGrid';
import useAuth from '../store/useAuth';
import useCredits from '../store/useCredits';
import useCreations from '../store/useCreations';
import { COLORS, IMAGE_SIZES, CREDIT_COSTS } from '../utils/constants';

// Motion components
const MotionBox = motion(Box);
const MotionButton = motion(Button);
const MotionImage = motion(ChakraImage);

  // Loading dots animation
  const LoadingDots = () => (
    <span className="loading-dots">
      <span></span>
      <span></span>
      <span></span>
    </span>
  );

  // Minimal loading spinner
  const ImageSkeleton = () => (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      h="320px"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"

      borderRadius="md"
    >
      <VStack spacing={4}>
        <Box
          w="40px"
          h="40px"
          border="3px solid"
          borderColor={COLORS.primary}
          borderRadius="full"
          borderTopColor="transparent"
          animation="spin 1s linear infinite"
          sx={{
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' }
            }
          }}
        />
        <Text 
          color={COLORS.primary} 
          fontSize="md" 
          fontWeight="medium"
          textAlign="center"
        >
          Generating…
        </Text>
      </VStack>
    </MotionBox>
  );

// Style presets for DALL-E 3 prompt enhancement
const STYLE_PRESETS = [
  { value: "photorealistic", label: "Photorealistic" },
  { value: "digital-art", label: "Digital Art" },
  { value: "anime", label: "Anime" },
  { value: "3d-render", label: "3D Render" },
  { value: "oil-painting", label: "Oil Painting" },
  { value: "concept-art", label: "Concept Art" },
  { value: "pixel-art", label: "Pixel Art" },
  { value: "cinematic", label: "Cinematic" },
];

function Image() {
  const [prompt, setPrompt] = useState('');
  const [imageSize, setImageSize] = useState('1024x1024');
  const [stylePreset, setStylePreset] = useState('photorealistic');
  const [enhancePrompt, setEnhancePrompt] = useState(true);
  
  const [generatedImage, setGeneratedImage] = useState('');
  const [generatedImageData, setGeneratedImageData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [recentImages, setRecentImages] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  const { isOpen: isLightboxOpen, onOpen: onLightboxOpen, onClose: onLightboxClose } = useDisclosure();
  
  const navigate = useNavigate();
  const toast = useToast();
  
  const { user, isLoggedIn } = useAuth();

  // Load from database when user is available
  useEffect(() => {
    async function loadRecentImages() {
      if (user?.walletAddress) {
        setIsLoadingHistory(true);
        try {
          const result = await fetchGenerationHistory(user.walletAddress, 12, 0);
          if (result.success) {
            const imageGenerations = result.generations
              .filter(gen => gen.generation_type === 'image' || !gen.generation_type) // Include legacy entries without type
              .map(mapGenerationToViewModel);
            setRecentImages(imageGenerations);
            
            // Optional: Cache for offline mode (but never as primary source)
            try {
              localStorage.setItem(`recent_images_cache_${user.walletAddress}`, JSON.stringify(imageGenerations));
            } catch (e) {
              console.warn('Failed to cache recent images:', e.message);
            }
          } else {
            console.error('Failed to load recent images:', result.error);
            // Fallback to cached data only if API fails
            try {
              const cached = localStorage.getItem(`recent_images_cache_${user.walletAddress}`);
              if (cached) {
                setRecentImages(JSON.parse(cached));
              }
            } catch (e) {
              console.warn('Failed to load cached images:', e.message);
            }
          }
        } catch (error) {
          console.error('Error loading recent images:', error);
        } finally {
          setIsLoadingHistory(false);
        }
      }
    }
    
    loadRecentImages();
  }, [user?.walletAddress]);

  // Remove localStorage saving logic - data comes from database
  const { balance, deductCredits } = useCredits();
  const { addCreation } = useCreations();
  
  // Animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);
  
  // Redirect to login if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
    }
  }, [isLoggedIn, navigate]);

  // Fetch credits when user is available
  useEffect(() => {
    if (user && user.walletAddress) {
      const { fetchBalance } = useCredits.getState();
      fetchBalance(user.walletAddress);
    }
  }, [user]);
  
  // Get credit cost based on selected size
  const getCreditCost = () => {
    const selectedSize = IMAGE_SIZES.find(size => size.value === imageSize);
    return selectedSize ? selectedSize.creditCost : CREDIT_COSTS.STANDARD_IMAGE;
  };

  // Handle lightbox navigation
  const openLightbox = (index) => {
    setSelectedImageIndex(index);
    onLightboxOpen();
  };

  const navigateLightbox = (direction) => {
    const newIndex = direction === 'next' 
      ? (selectedImageIndex + 1) % recentImages.length
      : (selectedImageIndex - 1 + recentImages.length) % recentImages.length;
    setSelectedImageIndex(newIndex);
  };
  
  // Download image function (supports data URI & remote URL)
  const handleDownload = async () => {
    if (!generatedImage) return;

    try {
      let blob;
      if (generatedImage.startsWith('data:')) {
        // Convert base64 to blob
        const arr = generatedImage.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        blob = new Blob([u8arr], { type: mime });
      } else {
        // Fetch remote URL then get blob
        const response = await fetch(generatedImage);
        blob = await response.blob();
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ai-generated-image-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: 'Image Downloaded',
        description: 'Check your downloads folder.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: 'Download Failed',
        description: 'Unable to download the image. Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };
  
  // Share image function
  const handleShare = async () => {
    if (!generatedImage) return;
    
    try {
      if (navigator.share && navigator.canShare) {
        await navigator.share({
          title: 'AI Generated Image',
          text: `Check out this AI-generated image: "${generatedImageData?.originalPrompt || prompt}"`,
          url: generatedImage,
        });
      } else {
        // Fallback: copy link to clipboard
        await navigator.clipboard.writeText(generatedImage);
        toast({
          title: 'Link Copied',
          description: 'Image link copied to clipboard',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Share Failed',
        description: 'Failed to share image. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };
  
  const handleGenerateImage = async () => {
    if (prompt.trim() === '') {
      toast({
        title: 'Error',
        description: 'Please enter a prompt',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Check if user has enough credits
    if (balance < getCreditCost()) {
      toast({
        title: 'Insufficient Credits',
        description: `You need ${getCreditCost()} credits to generate an image. You have ${balance} credits.`,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    
    setIsGenerating(true);
    setGeneratedImage('');
    setGeneratedImageData(null);
    
    try {
      // Deduct credits from backend first
      const creditResult = await deductCredits(user.walletAddress, getCreditCost(), 'usage', `img_${Date.now()}`);
      
      if (!creditResult.success) {
        throw new Error(creditResult.error || 'Failed to deduct credits');
      }
      
      // Prepare options for AI service
      const options = {
        stylePreset: stylePreset,
        enhancePrompt: enhancePrompt,
      };
      
      // Generate image using OpenAI DALL-E 3
      const result = await generateImage(prompt, imageSize, user.walletAddress, options);
      
      // Set the generated image and data
      setGeneratedImage(result.imageUrl);
      setGeneratedImageData(result);
      
      // Add to recent images with proper view model structure
      const resultViewModel = {
        id: `temp_${Date.now()}`, // Temporary ID until database saves
        url: result.imageUrl,
        prompt: result.originalPrompt || prompt,
        type: 'image',
        created_at: new Date().toISOString(),
        dimensions: imageSize,
        style: stylePreset,
        metadata: {
          revised_prompt: result.revisedPrompt,
          enhanced_prompt: result.enhancedPrompt
        },
        timestamp: Date.now()
      };
      
      setRecentImages(prev => {
        const list = [resultViewModel, ...prev];
        // Cache the updated list
        try {
          localStorage.setItem(`recent_images_cache_${user.walletAddress}`, JSON.stringify(list.slice(0, 40)));
        } catch (e) {
          console.warn('Failed to cache recent images:', e.message);
        }
        return list;
      });
      
      // Add to creations with enhanced data
      addCreation({
        url: result.imageUrl,
        prompt: result.originalPrompt,
        enhancedPrompt: result.enhancedPrompt,
        revisedPrompt: result.revisedPrompt,
        parameters: result.parameters,
        type: 'image',
        timestamp: result.timestamp,
        size: imageSize,
        creditCost: getCreditCost()
      });
      
      // Show success message with revised prompt info
      let successMessage = 'Your image has been generated successfully!';
      if (result.revisedPrompt && result.revisedPrompt !== result.enhancedPrompt) {
        successMessage += ' DALL-E refined your prompt for better results.';
      }
      
      toast({
        title: 'Image Generated',
        description: successMessage,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      
    } catch (error) {
      console.error("Image generation failed:", error);
      
      // Try to refund credits if generation failed
      try {
        await fetch(`${getBackendUrl()}/api/credits/refund`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            walletAddress: user.walletAddress,
            amount: getCreditCost(),
            reason: 'Image generation failed'
          }),
        });
      } catch (refundError) {
        console.warn('Failed to refund credits:', refundError);
      }
      
      // Determine error message based on error type
      let errorMessage = 'Failed to generate image. Please try again.';
      
      if (error.message.includes('insufficient_quota')) {
        errorMessage = 'OpenAI API quota exceeded. Please try again later.';
      } else if (error.message.includes('rate_limit')) {
        errorMessage = 'Too many requests. Please wait a moment and try again.';
      } else if (error.message.includes('content_policy')) {
        errorMessage = 'Your prompt violates content policy. Please modify your prompt.';
      } else if (error.message.includes('API key')) {
        errorMessage = 'Service temporarily unavailable. Please try again later.';
      }
      
      toast({
        title: 'Generation Failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleViewCreations = () => {
    navigate('/gallery');
  };
  
  // Enhanced loading spinner component
  const GeneratingOverlay = () => (
    <Box 
      position="absolute" 
      top="0" 
      left="0" 
      right="0" 
      bottom="0" 
      bg="rgba(0, 0, 0, 0.65)" 
      display="flex" 
      flexDirection="column"
      alignItems="center" 
      justifyContent="center" 
      zIndex="2"
    >
      <Spinner thickness="4px" speed="0.8s" color={COLORS.primary} size="xl" />
      <Text mt={4} color={COLORS.text} fontWeight="bold">
        Generating your masterpiece...
      </Text>
    </Box>
  );
  
  return (
    <Container maxW="6xl" py={{ base: 8, md: 12 }} px={{ base: 4, md: 6 }}>
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <VStack spacing={{ base: 4, md: 8 }} align="stretch">
          <Flex 
            justify="space-between" 
            align="center"
            direction={{ base: 'column', sm: 'row' }}
            gap={{ base: 3, sm: 0 }}
          >
            <Flex align="center">
              <Heading 
                as="h1" 
                size={{ base: 'lg', md: 'xl' }}
                color="white"
                letterSpacing="tight"
                fontWeight="bold"
              >
                AI Image Generator
              </Heading>
            </Flex>
            
            <Badge 
              px={{ base: 3, md: 4 }}
              py={{ base: 1.5, md: 2 }}
              borderRadius="md" 
              fontWeight="bold"
              bg="rgba(30, 220, 11, 0.15)"
              border="1px solid"
              borderColor={COLORS.primary}
              color={COLORS.accent}
              className="pulse"
              fontSize={{ base: 'xs', md: 'sm' }}
            >
              Credits: {balance}
            </Badge>
          </Flex>
          
          <Flex direction={{ base: 'column', md: 'row' }} gap={{ base: 4, md: 6 }}>
            {/* Left side: Form */}
            <Box 
              flex="1" 
              bg="linear-gradient(235.162deg, rgba(0, 0, 0, 0.85) 0%, rgba(2, 39, 8, 0.3) 50%, rgba(0, 0, 0, 0.85) 100%)"
              p={{ base: 4, md: 6 }}
              sx={{
                '--border-top-width': '0.5px',
                '--border-right-width': '0.5px',
                '--border-bottom-width': '0.5px',
                '--border-left-width': '0.5px',
                '--border-style': 'dashed',
                '--border-color': 'rgb(7, 74, 0)',
                'borderWidth': 'var(--border-top-width) var(--border-right-width) var(--border-bottom-width) var(--border-left-width)',
                'borderStyle': 'var(--border-style)',
                'borderColor': 'var(--border-color)'
              }}
              borderRadius="20px"
              className="glow-border"
              boxShadow="rgb(32, 230, 13) 0px 0px 15px -8px"
              backdropFilter="blur(10px)"
              position="relative"
              zIndex="1"
              transition="all 0.3s ease-in-out"
              _hover={{
                boxShadow: '0 15px 35px rgba(32, 230, 13, 0.4)',
                borderColor: 'rgba(32, 230, 13, 0.6)'
              }}
            >
              <Stack spacing={5}>
                <FormControl isRequired>
                  <FormLabel color={COLORS.textSecondary}>Image Prompt</FormLabel>
                  <Textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Describe the image you want to generate in detail..."
                    size="lg"
                    rows={5}
                    bg="rgba(0, 0, 0, 0.2)"
                    border="1px solid"
                    borderColor="rgba(30, 220, 11, 0.3)"
                    _hover={{ borderColor: COLORS.accent }}
                    _focus={{ 
                      borderColor: COLORS.accent,
                      boxShadow: `0 0 0 1px ${COLORS.accent}`
                    }}
                    color={COLORS.text}
                    resize="none"
                  />
                </FormControl>

                <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                  <GridItem>
                    <FormControl>
                      <FormLabel color={COLORS.textSecondary} fontSize={{ base: 'sm', md: 'md' }}>
                        Style Preset
                      </FormLabel>
                      <Select 
                        value={stylePreset} 
                        onChange={(e) => setStylePreset(e.target.value)}
                        bg="rgba(0, 0, 0, 0.2)"
                        border="1px solid"
                        borderColor="rgba(30, 220, 11, 0.3)"
                        _hover={{ borderColor: COLORS.accent }}
                        _focus={{ 
                          borderColor: COLORS.accent,
                          boxShadow: `0 0 0 1px ${COLORS.accent}`
                        }}
                        color={COLORS.text}
                        fontSize={{ base: 'sm', md: 'md' }}
                      >
                        {STYLE_PRESETS.map((style) => (
                          <option key={style.value} value={style.value} style={{background: COLORS.background}}>
                            {style.label}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </GridItem>
                  
                  <GridItem>
                    <FormControl>
                      <FormLabel color={COLORS.textSecondary} fontSize={{ base: 'sm', md: 'md' }}>
                        Image Size & Quality
                      </FormLabel>
                      <Select 
                        value={imageSize} 
                        onChange={(e) => setImageSize(e.target.value)}
                        bg="rgba(0, 0, 0, 0.2)"
                        border="1px solid"
                        borderColor="rgba(30, 220, 11, 0.3)"
                        _hover={{ borderColor: COLORS.accent }}
                        _focus={{ 
                          borderColor: COLORS.accent,
                          boxShadow: `0 0 0 1px ${COLORS.accent}`
                        }}
                        color={COLORS.text}
                        fontSize={{ base: 'sm', md: 'md' }}
                      >
                        {IMAGE_SIZES.map((size) => (
                          <option key={size.value} value={size.value} style={{background: COLORS.background}}>
                            {size.label} - {size.creditCost} credit{size.creditCost > 1 ? 's' : ''}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </GridItem>
                </Grid>

                <FormControl>
                  <Flex justify="space-between" align="center">
                    <FormLabel color={COLORS.textSecondary} mb={0} fontSize="sm">Add Style & Quality Terms</FormLabel>
                    <Switch 
                      colorScheme="green" 
                      size="sm" 
                      isChecked={enhancePrompt}
                      onChange={() => setEnhancePrompt(!enhancePrompt)}
                    />
                  </Flex>
                  <Text fontSize="xs" color={COLORS.textSecondary} mt={2}>
                    Add technical style and quality terms to your prompt (e.g., "photorealistic, high resolution"). 
                    </Text>
                </FormControl>
                
                <MotionButton
                  mt={4}
                  height="60px"
                  fontSize="lg"
                  onClick={handleGenerateImage}
                  isLoading={isGenerating}
                  loadingText={<span>Generating <LoadingDots /></span>}
                  className="primary-button"
                  position="relative"
                  overflow="hidden"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                  disabled={isGenerating || balance < getCreditCost()}
                >
                  Generate ({getCreditCost()} credits)
                </MotionButton>
              </Stack>
            </Box>
            
            {/* Right side: Preview */}
            <Box 
              flex="1" 
              bg="linear-gradient(235.162deg, rgba(0, 0, 0, 0.85) 0%, rgba(2, 39, 8, 0.3) 50%, rgba(0, 0, 0, 0.85) 100%)"
              p={{ base: 4, md: 6 }}
              sx={{
                '--border-top-width': '0.5px',
                '--border-right-width': '0.5px',
                '--border-bottom-width': '0.5px',
                '--border-left-width': '0.5px',
                '--border-style': 'dashed',
                '--border-color': 'rgb(7, 74, 0)',
                'borderWidth': 'var(--border-top-width) var(--border-right-width) var(--border-bottom-width) var(--border-left-width)',
                'borderStyle': 'var(--border-style)',
                'borderColor': 'var(--border-color)'
              }}
              borderRadius="20px"
              className="glow-border"
              boxShadow="rgb(32, 230, 13) 0px 0px 15px -8px"
              backdropFilter="blur(10px)"
              position="relative"
              overflow="hidden"
              zIndex="1"
              transition="all 0.3s ease-in-out"
              _hover={{
                boxShadow: '0 15px 35px rgba(32, 230, 13, 0.4)',
                borderColor: 'rgba(32, 230, 13, 0.6)'
              }}
            >
              <Flex justify="space-between" align="center" mb={4}>
                <Text 
                  fontWeight="bold" 
                  color={COLORS.text}
                  textShadow="0 0 5px rgba(30, 220, 11, 0.5)"
                >
                  {generatedImage ? 'Generated Image' : 'Preview Area'}
                </Text>
                
                {generatedImage && (
                  <HStack spacing={2}>
                    <Badge colorScheme="green" variant="subtle" px={2}>
                      {imageSize}
                    </Badge>
                    <Badge colorScheme="green" variant="subtle" px={2}>
                      {stylePreset}
                    </Badge>
                  </HStack>
                )}
              </Flex>
              
              <MotionBox 
                h="320px" 
                bg="rgba(0, 0, 0, 0.3)" 
                borderRadius="md" 
                display="flex"
                alignItems="center"
                justifyContent="center"
                overflow="hidden"
                borderWidth="1px"
                borderColor="rgba(30, 220, 11, 0.2)"
                position="relative"
                whileHover={{ borderColor: "rgba(30, 220, 11, 0.4)" }}
                transition={{ duration: 0.3 }}
              >
                <AnimatePresence mode="wait">
                  {isGenerating ? (
                    <ImageSkeleton key="skeleton" />
                  ) : generatedImage ? (
                    <MotionImage
                      key="image"
                      src={generatedImage} 
                      alt="Generated AI image" 
                      objectFit="contain"
                      maxH="320px"
                      borderRadius="md"
                      boxShadow="0 4px 20px rgba(0, 0, 0, 0.4)"
                      cursor="pointer"
                      onClick={() => openLightbox(0)}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      whileHover={{ scale: 1.02 }}
                    />
                  ) : (
                    <MotionBox 
                      key="placeholder"
                      textAlign="center" 
                      p={4}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Text color={COLORS.textSecondary} mb={3}>
                        Your generated image will appear here
                      </Text>
                      <Box 
                        width="120px" 
                        height="120px" 
                        borderRadius="md" 
                        border="1px dashed" 
                        borderColor={COLORS.primary} 
                        opacity="0.5"
                        mx="auto"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text 
                          fontSize="5xl" 
                          color={COLORS.primary}
                          opacity="0.7"
                        >
                          ?
                        </Text>
                      </Box>
                    </MotionBox>
                  )}
                </AnimatePresence>
              </MotionBox>
              
              {generatedImage ? (
                <VStack mt={4} spacing={3} align="stretch">
                  <Wrap spacing={{ base: 2, md: 3 }} justify="center">
                    <WrapItem>
                      <Button
                        onClick={handleDownload}
                        size={{ base: 'xs', md: 'sm' }}
                        borderColor={COLORS.primary}
                        color={COLORS.primary}
                        variant="outline"
                        _hover={{
                          bg: "rgba(30, 220, 11, 0.1)",
                        }}
                      >
                        Download
                      </Button>
                    </WrapItem>
                    
                    <WrapItem>
                      <Button
                        onClick={handleShare}
                        size={{ base: 'xs', md: 'sm' }}
                        borderColor={COLORS.primary}
                        color={COLORS.primary}
                        variant="outline"
                        _hover={{
                          bg: "rgba(30, 220, 11, 0.1)",
                        }}
                      >
                        Share
                      </Button>
                    </WrapItem>
                    
                    <WrapItem>
                      <Button
                        onClick={handleViewCreations}
                        size={{ base: 'xs', md: 'sm' }}
                        borderColor={COLORS.primary}
                        color={COLORS.primary}
                        variant="outline"
                        _hover={{
                          bg: "rgba(30, 220, 11, 0.1)",
                        }}
                      >
                        View All
                      </Button>
                    </WrapItem>
                  </Wrap>
                  
                  {generatedImageData?.revisedPrompt && (
                    <Box p={3} bg="rgba(30, 220, 11, 0.1)" borderRadius="md" borderLeft="3px solid" borderColor={COLORS.primary}>
                      <Text fontSize="xs" color={COLORS.textSecondary} mb={1}>DALL-E 3 Enhanced Prompt:</Text>
                      <Text fontSize="sm" color={COLORS.text}>{generatedImageData.revisedPrompt}</Text>
                    </Box>
                  )}
                </VStack>
              ) : (
                <VStack mt={4} spacing={1}>
                  <Text fontSize="xs" color={COLORS.textSecondary}>
                    Try these example prompts:
                  </Text>
                  <Wrap spacing={2} justify="center">
                    <WrapItem>
                      <Badge 
                        colorScheme="green" 
                        color={"green"}
                        borderRadius={"5px"}
                        background={"black"}
                        variant="subtle" 
                        px={2} 
                        py={1} 
                        cursor="pointer"
                        onClick={() => setPrompt("A futuristic city with flying cars and neon lights")}
                        _hover={{ opacity: 0.8 }}
                      >
                        Futuristic City
                      </Badge>
                    </WrapItem>
                    <WrapItem>
                      <Badge 
                        colorScheme="green" 
                        color={"green"}
                        borderRadius={"5px"}
                        background={"black"}
                        variant="subtle" 
                        px={2} 
                        py={1} 
                        cursor="pointer"
                        onClick={() => setPrompt("A magical forest with glowing mushrooms and fairy lights")}
                        _hover={{ opacity: 0.8 }}
                      >
                        Magical Forest
                      </Badge>
                    </WrapItem>
                    <WrapItem>
                      <Badge 
                        colorScheme="green" 
                        color={"green"}
                        borderRadius={"5px"}
                        background={"black"}
                        variant="subtle" 
                        px={2} 
                        py={1} 
                        cursor="pointer"
                        onClick={() => setPrompt("A cyberpunk character with XRP-themed augmentations")}
                        _hover={{ opacity: 0.8 }}
                      >
                        XRP Cyberpunk
                      </Badge>
                    </WrapItem>
                  </Wrap>
                </VStack>
              )}

              {/* Status indicator */}
              <Box position="absolute" bottom="10px" right="10px">
                <Flex>
                  <Box 
                    w="6px" 
                    h="6px" 
                    borderRadius="full" 
                    bg={COLORS.primary} 
                    mr="4px"
                    opacity={isGenerating ? "1" : "0.4"}
                    sx={
                      isGenerating ? {
                        animation: "pulse 2s infinite",
                        "@keyframes pulse": {
                          "0%": { boxShadow: "0 0 0 0 rgba(30, 220, 11, 0.4)" },
                          "70%": { boxShadow: "0 0 0 10px rgba(30, 220, 11, 0)" },
                          "100%": { boxShadow: "0 0 0 0 rgba(30, 220, 11, 0)" }
                        }
                      } : {}
                    }
                  />
                  <Box 
                    w="6px" 
                    h="6px" 
                    borderRadius="full" 
                    bg={COLORS.accent} 
                    opacity={generatedImage ? "1" : "0.4"}
                  />
                </Flex>
              </Box>
            </Box>
          </Flex>

          {/* Recent Images History (Database-backed) */}
          {recentImages.length > 0 && (
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Heading size="md" color={COLORS.text} mb={4}>
                Recent Generations
              </Heading>
              {isLoadingHistory ? (
                <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing={4}>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <Skeleton key={index} height="120px" borderRadius="md" />
                  ))}
                </SimpleGrid>
              ) : (
                <RecentHistoryGrid items={recentImages} limit={12} />
              )}
            </MotionBox>
          )}
        </VStack>
      </MotionBox>

      {/* Lightbox Modal - Only shows current generated image */}
      <Modal isOpen={isLightboxOpen} onClose={onLightboxClose} size={{ base: 'full', md: '6xl' }} isCentered>
        <ModalOverlay bg="rgba(0, 0, 0, 0.9)" backdropFilter="blur(10px)" />
        <ModalContent 
          bg="transparent" 
          boxShadow="none" 
          maxW={{ base: '100vw', md: '90vw' }}
          maxH={{ base: '100vh', md: '90vh' }}
          m={{ base: 0, md: 'auto' }}
        >
          <ModalCloseButton 
            color="white" 
            size={{ base: 'md', md: 'lg' }}
            top={{ base: 2, md: 4 }}
            right={{ base: 2, md: 4 }}
            zIndex={10} 
          />
          <ModalBody p={0} display="flex" alignItems="center" justifyContent="center">
            {generatedImage && (
              <Box position="relative" maxW="100%" maxH="100%">
                <MotionImage
                  src={generatedImage}
                  alt={generatedImageData?.originalPrompt || prompt}
                  maxW="100%"
                  maxH="90vh"
                  objectFit="contain"
                  borderRadius="md"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                />

                {/* Action buttons */}
                <HStack position="absolute" bottom={4} right={4} spacing={2}>
                  <IconButton
                    icon={<FaVideo />}
                    onClick={() => {
                      localStorage.setItem('selected_image_for_video', JSON.stringify({
                        url: generatedImage,
                        prompt: generatedImageData?.originalPrompt || prompt,
                        timestamp: Date.now()
                      }));
                      navigate('/video');
                      onLightboxClose();
                    }}
                    bg="rgba(30, 220, 11, 0.8)"
                    color="white"
                    _hover={{ bg: "rgba(30, 220, 11, 0.8)" }}
                    size="lg"
                    aria-label="Use for video generation"
                  />
                  <IconButton
                    icon={<FaDownload />}
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = generatedImage;
                      link.download = `ai-image-${Date.now()}.png`;
                      link.click();
                    }}
                    bg="rgba(30, 220, 11, 0.8)"
                    color="white"
                    _hover={{ bg: "rgba(30, 220, 11, 1)" }}
                    size="lg"
                    aria-label="Download image"
                  />
                </HStack>

                {/* Image info */}
                <Box
                  position="absolute"
                  bottom={4}
                  left={4}
                  bg="rgba(0, 0, 0, 0.8)"
                  p={3}
                  borderRadius="md"
                  maxW="400px"
                >
                  <Text color="white" fontSize="sm" fontWeight="bold" mb={1}>
                    {generatedImageData?.originalPrompt || prompt}
                  </Text>

                </Box>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Container>
  );
}

export default Image; 